import React, { useEffect, useState } from "react";
import { Image } from "react-bootstrap";
import {
  StreakConfigList,
  StreakStatus,
} from "@nestcoinco/hyper-api-gateway-api-client";
import { useTranslation } from "react-i18next";

import "./StreaksSection.scss";
import TooltipButton from "../../TooltipButton/TooltipButton";
import ImageHourglass from "../../../../images/hourglass.svg";
import Gem from "../../../Gem/Gem";
import GhostButton from "../../GhostButton/GhostButton";
import StreakExpiringModal from "../../modals/StreakExpiringModal/StreakExpiringModal";
import StreakModal from "../../modals/StreakModal/StreakModal";
import StreakDays from "../../StreakDays/StreakDays";
import StreakInfoModal from "../../modals/StreakInfoModal/StreakInfoModal";
import {
  handleGetAvailableStreaks,
  handleGetStreakStatus,
} from "../../../../api-clients/streaksClient";
import {
  TimeUnit,
  getEndOfDay,
  getTimeDifferenceToNow,
} from "../../../../utils/helpers";
import Skeleton from "../../../Skeleton/Skeleton";

const SHOW_TIME_LEFT_THRESHOLD = 6;

function LoadingSkeleton() {
  return (
    <div className="px-3 mb-3">
      <Skeleton height={164} />
    </div>
  );
}

function StreaksSection() {
  const { t } = useTranslation("dashboard");
  const [isExpiringModalOpen, setIsExpiringModalOpen] = useState(false);
  const [isStreakModalOpen, setIsStreakModalOpen] = useState(false);
  const [isStreakInfoModalOpen, setIsStreakInfoModalOpen] = useState(false);
  const [streakStatus, setStreakStatus] = useState<StreakStatus>();
  const [streakConfig, setStreakConfig] = useState<StreakConfigList>();
  const [isLoading, setIsLoading] = useState(true);

  const todaysReward =
    streakConfig &&
    streakStatus &&
    streakConfig.records.find(
      (record) => record.milestone === streakStatus.streakCount + 1
    );

  const timeLeft = getTimeDifferenceToNow(getEndOfDay());
  const canShowTimeLeft =
    ((timeLeft?.unit === TimeUnit.HOUR &&
      timeLeft.difference <= SHOW_TIME_LEFT_THRESHOLD) ||
      timeLeft?.unit === TimeUnit.MINUTE) &&
    !!streakStatus?.streakCount;

  let timeLeftCopy = "";
  if (timeLeft) {
    timeLeftCopy = `${timeLeft.difference} ${timeLeft.unit}${
      timeLeft.difference > 1 ? "s" : ""
    }`;
  }

  useEffect(() => {
    const getStreakStatus = async () => {
      const [streak, streakConfigList] = await Promise.all([
        handleGetStreakStatus(),
        handleGetAvailableStreaks(),
      ]);
      setStreakConfig(streakConfigList);
      setStreakStatus(streak);
      setIsLoading(false);
    };

    getStreakStatus();
  }, []);

  if (isLoading) return <LoadingSkeleton />;

  if (!streakStatus || streakStatus.todayCompleted) return null;

  return (
    <section className="streaks-section px-3">
      <div className="heading-container d-flex justify-content-between align-items-center">
        <h2 className="heading">Streaks</h2>
        <TooltipButton onClick={() => setIsStreakInfoModalOpen(true)} />
      </div>

      <div className="position-relative">
        <GhostButton
          aria-label="view streak info"
          className="w-100 p-0 flex-column"
          onClick={() => setIsStreakModalOpen(true)}
        >
          <div className="days-container w-100">
            <StreakDays status={streakStatus} />
          </div>
          {todaysReward && (
            <div className="copy w-100">
              {t("play_a_game_and_win")}{" "}
              <strong>
                <Gem width={20} height={20} /> {todaysReward.amount}
              </strong>{" "}
              {t("today")}
            </div>
          )}
        </GhostButton>
        {canShowTimeLeft && (
          <GhostButton
            className="time-left"
            onClick={() => setIsExpiringModalOpen(true)}
          >
            <Image src={ImageHourglass} alt="hourglass" className="hourglass" />{" "}
            <span>{timeLeftCopy}</span>
          </GhostButton>
        )}
      </div>

      <StreakExpiringModal
        show={isExpiringModalOpen}
        onHide={() => setIsExpiringModalOpen(false)}
        time={timeLeftCopy}
      />
      {isStreakModalOpen && streakConfig && (
        <StreakModal
          show={isStreakModalOpen}
          onHide={() => setIsStreakModalOpen(false)}
          status={streakStatus}
          config={streakConfig}
        />
      )}
      <StreakInfoModal
        show={isStreakInfoModalOpen}
        onHide={() => setIsStreakInfoModalOpen(false)}
      />
    </section>
  );
}

export default StreaksSection;
